// store.jsx — central app state for Lift Log (working prototype layer)
//
// A single React context providing:
//   - user       { loggedIn, email, name, pro, trialEndsAt }
//   - programs   [{id,name,days:[{id,name,exercises:[...]}]}]
//   - activeProgramId
//   - sessions   [{id,programId,dayId,startedAt,completedAt,sets:[{exerciseId,setIdx,w,reps,rpe,ts}]}]
//   - exercises  library (built-in + custom)
//   - bodyEntries
//   - prefs      (units, restDefault, haptics, theme, notifs, etc.)
//   - coach      (clients list, messages)
//   - route      ('auth' | 'home' | 'program/:id' | 'exercise/:id' | ...)
//
// State is persisted to IndexedDB (src/persist.js: one record per finished
// workout + one for the rest, debounced and batched) with a localStorage
// mirror under 'liftlog.v1' for a synchronous first render. Anyone can call
// useApp() to read or dispatch. Test seam: see persist.js (an external
// 'liftlog.v1' is imported on load; window.__liftlog.seed(state)).

import React from 'react';
import { deletePhoto } from './photos.jsx';
import { importPhotoData, restoredPhotos } from './photos.jsx'; // backups that embed progress photos
import { prescriptionFor, withSetCount, weightIncrementFor, localDateStr, wouldOrphanActiveSession } from './utils.jsx';
import { sessionSlots, slotFromProgramExercise, applyProgression, programWithSessionStructure } from './session_model.js';
import { weekStart } from './analytics.js';
import { handleSheetPop } from './sheet_history.js';
import { disableSync, configureSyncDefaults, resetSyncTracking, programContentHash, recordTimes } from './sync.js';
import { computePRs as prsFrom, sessionBests, PRS_VERSION } from './prs.js';
import { sanitizeSet, toEpochMs } from './validate.js';
import { historyOf } from './history.js';
import * as persist from './persist.js';
import { programWeekInfo, prescribe, schemeOf, afterSession } from './progression.js';
import { progressionDelta, programBefore, reconcileProgression, limitDelta, legacyDelta } from './progression_undo.js';

const LS_KEY = 'liftlog.v1';
const ROUTE_KEY = 'liftlog.route';

// Deterministic id
const uid = (p = '') => p + Math.random().toString(36).slice(2, 8);

// ——— Built-in exercise library (30 common exercises) ———
const BUILTIN_EXERCISES = [
  // Chest
  { id: 'x_incsmith', name: 'Incline Smith Press', group: 'Chest', equipment: 'Smith', secondary: ['Shoulders', 'Triceps'], mechanic: 'compound', yt: 'https://youtu.be/8urE8Z8AMQ4' },
  { id: 'x_flatdb',   name: 'Flat DB Bench Press', group: 'Chest', equipment: 'Dumbbell', secondary: ['Shoulders', 'Triceps'], mechanic: 'compound', yt: 'https://youtu.be/YQ2s_Y7g5Qk' },
  { id: 'x_pecdeck',  name: 'Pec Deck', group: 'Chest', equipment: 'Machine', secondary: [], mechanic: 'isolation', yt: '' },
  { id: 'x_bench',    name: 'Barbell Bench Press', group: 'Chest', equipment: 'Barbell', secondary: ['Shoulders', 'Triceps'], mechanic: 'compound', yt: 'https://youtu.be/rT7DgCr-3pg' },
  { id: 'x_cflies',   name: 'Cable Flies', group: 'Chest', equipment: 'Cable', secondary: ['Shoulders'], mechanic: 'isolation', yt: '' },
  // Back
  { id: 'x_pullup',   name: 'Weighted Pull-up', group: 'Back', equipment: 'Bodyweight+', secondary: ['Biceps'], mechanic: 'compound', yt: 'https://youtu.be/eGo4IYlbE5g' },
  { id: 'x_csrow',    name: 'Chest-Supported Row', group: 'Back', equipment: 'Dumbbell', secondary: ['Biceps', 'Shoulders'], mechanic: 'compound', yt: '' },
  { id: 'x_lat',      name: 'Lat Pulldown (wide)', group: 'Back', equipment: 'Cable', secondary: ['Biceps'], mechanic: 'compound', yt: '' },
  { id: 'x_cablerow', name: 'Cable Row (neutral)', group: 'Back', equipment: 'Cable', secondary: ['Biceps', 'Traps'], mechanic: 'compound', yt: '' },
  { id: 'x_dead',     name: 'Conventional Deadlift', group: 'Back', equipment: 'Barbell', secondary: ['Glutes', 'Hamstrings', 'Lower Back', 'Traps', 'Forearms'], mechanic: 'compound', yt: 'https://youtu.be/op9kVnSso6Q' },
  // Shoulders
  { id: 'x_ohp',      name: 'Overhead Press', group: 'Shoulders', equipment: 'Barbell', secondary: ['Triceps'], mechanic: 'compound', yt: '' },
  { id: 'x_dbohp',    name: 'Standing DB Shoulder Press', group: 'Shoulders', equipment: 'Dumbbell', secondary: ['Triceps'], mechanic: 'compound', yt: '' },
  { id: 'x_mohp',     name: 'Machine Shoulder Press', group: 'Shoulders', equipment: 'Machine', secondary: ['Triceps'], mechanic: 'compound', yt: '' },
  { id: 'x_lat_r',    name: 'Cable Lateral Raise', group: 'Shoulders', equipment: 'Cable', secondary: [], mechanic: 'isolation', yt: '' },
  { id: 'x_facepull', name: 'Face Pull', group: 'Shoulders', equipment: 'Cable', secondary: ['Traps', 'Back'], mechanic: 'isolation', yt: '' },
  // Legs
  { id: 'x_squat',    name: 'Back Squat', group: 'Quads', equipment: 'Barbell', secondary: ['Glutes', 'Hamstrings', 'Lower Back'], mechanic: 'compound', yt: 'https://youtu.be/SW_C1A-rejs' },
  { id: 'x_fsquat',   name: 'Front Squat', group: 'Quads', equipment: 'Barbell', secondary: ['Glutes', 'Core'], mechanic: 'compound', yt: '' },
  { id: 'x_hacksq',   name: 'Hack Squat', group: 'Quads', equipment: 'Machine', secondary: ['Glutes'], mechanic: 'compound', yt: '' },
  { id: 'x_legpress', name: 'Leg Press', group: 'Quads', equipment: 'Machine', secondary: ['Glutes', 'Hamstrings'], mechanic: 'compound', yt: '' },
  { id: 'x_bss',      name: 'Bulgarian Split Squat', group: 'Quads', equipment: 'Dumbbell', secondary: ['Glutes', 'Hamstrings'], mechanic: 'compound', yt: '' },
  { id: 'x_rdl',      name: 'Romanian Deadlift', group: 'Hamstrings', equipment: 'Barbell', secondary: ['Glutes', 'Lower Back'], mechanic: 'compound', yt: '' },
  { id: 'x_slc',      name: 'Seated Leg Curl', group: 'Hamstrings', equipment: 'Machine', secondary: [], mechanic: 'isolation', yt: '' },
  { id: 'x_llc',      name: 'Lying Leg Curl', group: 'Hamstrings', equipment: 'Machine', secondary: [], mechanic: 'isolation', yt: '' },
  { id: 'x_hipthr',   name: 'Hip Thrust', group: 'Glutes', equipment: 'Barbell', secondary: ['Hamstrings'], mechanic: 'compound', yt: '' },
  { id: 'x_scalf',    name: 'Standing Calf Raise', group: 'Calves', equipment: 'Machine', secondary: [], mechanic: 'isolation', yt: '' },
  { id: 'x_sitcalf',  name: 'Seated Calf Raise', group: 'Calves', equipment: 'Machine', secondary: [], mechanic: 'isolation', yt: '' },
  // Arms
  { id: 'x_ezcurl',   name: 'EZ-Bar Curl', group: 'Biceps', equipment: 'Barbell', secondary: ['Forearms'], mechanic: 'isolation', yt: '' },
  { id: 'x_preach',   name: 'Preacher Curl', group: 'Biceps', equipment: 'Machine', secondary: [], mechanic: 'isolation', yt: '' },
  { id: 'x_hammer',   name: 'Hammer Curl', group: 'Biceps', equipment: 'Dumbbell', secondary: ['Forearms'], mechanic: 'isolation', yt: '' },
  { id: 'x_push',     name: 'Cable Triceps Pushdown', group: 'Triceps', equipment: 'Cable', secondary: [], mechanic: 'isolation', yt: '' },
  { id: 'x_ohext',    name: 'Overhead Cable Extension', group: 'Triceps', equipment: 'Cable', secondary: [], mechanic: 'isolation', yt: '' },
  { id: 'x_skull',    name: 'Skull Crushers', group: 'Triceps', equipment: 'Barbell', secondary: [], mechanic: 'isolation', yt: '' },
  // Core
  { id: 'x_plank',    name: 'Weighted Plank', group: 'Core', equipment: 'Bodyweight+', secondary: [], mechanic: 'isolation', yt: '', unit: 'sec' },
  { id: 'x_abwheel',  name: 'Ab Wheel Rollout', group: 'Core', equipment: 'Other', secondary: ['Shoulders'], mechanic: 'compound', yt: '' },
  { id: 'x_hlr',      name: 'Hanging Leg Raise', group: 'Core', equipment: 'Bodyweight', secondary: ['Forearms'], mechanic: 'compound', yt: '' },
];

const BUILTIN_BY_ID = Object.fromEntries(BUILTIN_EXERCISES.map(e => [e.id, e]));

const MUSCLE_GROUPS_ALL = ['Chest', 'Back', 'Shoulders', 'Quads', 'Hamstrings', 'Glutes', 'Calves', 'Biceps', 'Triceps', 'Core'];
const EQUIPMENT_ALL = ['Barbell', 'Dumbbell', 'Machine', 'Cable', 'Smith', 'Bodyweight', 'Bodyweight+', 'Other'];

// ——— Starter program templates (Phase 21a) ———
// Called from WelcomeScreen when user picks a template. Each call produces
// a fresh program with new IDs, currentWeek=1, owned by the user. No preset
// loads: like the guided plans, each lift starts by finding a working weight.
function _toEx(xById, name, sets, repMin, repMax, rest) {
  const base = xById[name];
  return {
    id: uid('pe_'),
    exerciseId: base ? base.id : null,
    name,
    group: base ? base.group : 'Other',
    unit: (base && base.unit) || 'reps',
    sets, repMin, repMax,
    targetWeight: 0,
    rest,
    yt: base ? (base.yt || '') : '',
    alternates: [],
  };
}

function buildStarterUpperLower() {
  const xById = Object.fromEntries(BUILTIN_EXERCISES.map(x => [x.name, x]));
  const toEx = (n, s, rMin, rMax, r) => _toEx(xById, n, s, rMin, rMax, r);
  return {
    id: uid('prog_'),
    name: 'Upper / Lower 4×',
    author: 'You',
    weeks: 12,
    currentWeek: 1,
    split: '4 days · Upper / Lower',
    days: [
      { id: uid('day_'), name: 'Upper A', day: 'Mon', exercises: [
        toEx('Incline Smith Press', 3, 6, 8, 180),
        toEx('Chest-Supported Row', 3, 8, 10, 150),
        toEx('Standing DB Shoulder Press', 2, 8, 10, 120),
        toEx('Lat Pulldown (wide)', 2, 10, 12, 120),
        toEx('Cable Lateral Raise', 3, 12, 15, 90),
        toEx('EZ-Bar Curl', 2, 8, 10, 90),
        toEx('Cable Triceps Pushdown', 2, 10, 12, 90),
      ] },
      { id: uid('day_'), name: 'Lower A', day: 'Tue', exercises: [
        toEx('Back Squat', 3, 5, 7, 210),
        toEx('Romanian Deadlift', 3, 8, 10, 180),
        toEx('Leg Press', 2, 10, 12, 150),
        toEx('Seated Leg Curl', 2, 10, 12, 90),
        toEx('Standing Calf Raise', 3, 8, 10, 90),
        toEx('Weighted Plank', 3, 30, 45, 60),
      ] },
      { id: uid('day_'), name: 'Upper B', day: 'Thu', exercises: [
        toEx('Weighted Pull-up', 3, 5, 7, 180),
        toEx('Flat DB Bench Press', 3, 8, 10, 150),
        toEx('Machine Shoulder Press', 2, 8, 10, 120),
        toEx('Cable Row (neutral)', 2, 10, 12, 120),
        toEx('Pec Deck', 2, 12, 15, 90),
        toEx('Preacher Curl', 2, 10, 12, 90),
        toEx('Overhead Cable Extension', 2, 10, 12, 90),
      ] },
      { id: uid('day_'), name: 'Lower B', day: 'Fri', exercises: [
        toEx('Conventional Deadlift', 2, 4, 6, 240),
        toEx('Bulgarian Split Squat', 3, 8, 10, 150),
        toEx('Hip Thrust', 3, 8, 10, 150),
        toEx('Hack Squat', 2, 10, 12, 120),
        toEx('Lying Leg Curl', 2, 10, 12, 90),
        toEx('Seated Calf Raise', 3, 12, 15, 60),
        toEx('Ab Wheel Rollout', 3, 8, 12, 60),
      ] },
    ],
  };
}

function buildStarterFullBody() {
  const xById = Object.fromEntries(BUILTIN_EXERCISES.map(x => [x.name, x]));
  const toEx = (n, s, rMin, rMax, r) => _toEx(xById, n, s, rMin, rMax, r);
  return {
    id: uid('prog_'),
    name: 'Full Body 3×',
    author: 'You',
    weeks: 12,
    currentWeek: 1,
    split: '3 days · Full Body',
    days: [
      { id: uid('day_'), name: 'Day A', day: 'Mon', exercises: [
        toEx('Back Squat', 3, 5, 7, 180),
        toEx('Flat DB Bench Press', 3, 6, 8, 150),
        toEx('Chest-Supported Row', 3, 8, 10, 120),
        toEx('Standing DB Shoulder Press', 2, 8, 10, 120),
        toEx('Weighted Plank', 3, 30, 45, 60),
      ] },
      { id: uid('day_'), name: 'Day B', day: 'Wed', exercises: [
        toEx('Conventional Deadlift', 2, 4, 6, 240),
        toEx('Incline Smith Press', 3, 6, 8, 180),
        toEx('Lat Pulldown (wide)', 3, 8, 10, 120),
        toEx('EZ-Bar Curl', 2, 8, 10, 90),
        toEx('Cable Triceps Pushdown', 2, 10, 12, 90),
      ] },
      { id: uid('day_'), name: 'Day C', day: 'Fri', exercises: [
        toEx('Back Squat', 3, 5, 7, 180),
        toEx('Weighted Pull-up', 3, 5, 7, 180),
        toEx('Overhead Press', 3, 6, 8, 150),
        toEx('Romanian Deadlift', 3, 8, 10, 150),
        toEx('Ab Wheel Rollout', 3, 8, 12, 60),
      ] },
    ],
  };
}

function buildStarterPPL() {
  const xById = Object.fromEntries(BUILTIN_EXERCISES.map(x => [x.name, x]));
  const toEx = (n, s, rMin, rMax, r) => _toEx(xById, n, s, rMin, rMax, r);
  return {
    id: uid('prog_'),
    name: 'Push / Pull / Legs 6×',
    author: 'You',
    weeks: 12,
    currentWeek: 1,
    split: '6 days · Push / Pull / Legs',
    days: [
      { id: uid('day_'), name: 'Push', day: 'Mon', exercises: [
        toEx('Barbell Bench Press', 4, 6, 8, 180),
        toEx('Standing DB Shoulder Press', 3, 8, 10, 120),
        toEx('Incline Smith Press', 3, 8, 10, 120),
        toEx('Cable Lateral Raise', 3, 12, 15, 60),
        toEx('Cable Triceps Pushdown', 3, 10, 12, 75),
      ] },
      { id: uid('day_'), name: 'Pull', day: 'Tue', exercises: [
        toEx('Weighted Pull-up', 4, 5, 7, 180),
        toEx('Chest-Supported Row', 3, 8, 10, 120),
        toEx('Lat Pulldown (wide)', 3, 10, 12, 90),
        toEx('Face Pull', 3, 12, 15, 60),
        toEx('EZ-Bar Curl', 3, 8, 10, 75),
      ] },
      { id: uid('day_'), name: 'Legs', day: 'Wed', exercises: [
        toEx('Back Squat', 4, 5, 7, 210),
        toEx('Romanian Deadlift', 3, 8, 10, 150),
        toEx('Leg Press', 3, 10, 12, 120),
        toEx('Seated Leg Curl', 3, 10, 12, 90),
        toEx('Standing Calf Raise', 4, 8, 10, 75),
      ] },
      { id: uid('day_'), name: 'Push', day: 'Thu', exercises: [
        toEx('Incline Smith Press', 4, 6, 8, 180),
        toEx('Overhead Press', 3, 8, 10, 120),
        toEx('Flat DB Bench Press', 3, 8, 10, 120),
        toEx('Pec Deck', 3, 12, 15, 60),
        toEx('Overhead Cable Extension', 3, 10, 12, 75),
      ] },
      { id: uid('day_'), name: 'Pull', day: 'Fri', exercises: [
        toEx('Conventional Deadlift', 3, 4, 6, 240),
        toEx('Cable Row (neutral)', 3, 8, 10, 120),
        toEx('Lat Pulldown (wide)', 3, 10, 12, 90),
        toEx('Face Pull', 3, 12, 15, 60),
        toEx('Hammer Curl', 3, 8, 10, 75),
      ] },
      { id: uid('day_'), name: 'Legs', day: 'Sat', exercises: [
        toEx('Front Squat', 4, 5, 7, 210),
        toEx('Bulgarian Split Squat', 3, 8, 10, 120),
        toEx('Lying Leg Curl', 3, 10, 12, 90),
        toEx('Hip Thrust', 3, 8, 10, 120),
        toEx('Ab Wheel Rollout', 3, 8, 12, 60),
      ] },
    ],
  };
}

// List of starter templates shown in WelcomeScreen.
// Each entry is { id, label, blurb, build() } where build returns a fresh
// program with new IDs every call (never reuse references).
const STARTER_PROGRAMS = [
  {
    id: 'upper-lower',
    label: 'Upper / Lower 4×',
    blurb: '4 days · alternating upper and lower body · 12-week progression',
    build: buildStarterUpperLower,
  },
  {
    id: 'full-body',
    label: 'Full Body 3×',
    blurb: '3 days · one full-body session per workout · great starter',
    build: buildStarterFullBody,
  },
  {
    id: 'ppl',
    label: 'Push / Pull / Legs 6×',
    blurb: '6 days · higher-volume split · for experienced lifters',
    build: buildStarterPPL,
  },
];

// ——— Default/initial state ———
// Phase 21a: no seed data. Fresh install = empty personal-use app.
// DEC-no-accounts-v2: loggedIn defaults to true (no account concept).
function defaultState() {
  return {
    user: { loggedIn: true, email: '', name: '', pro: false, trialEndsAt: null },
    welcomed: false,           // Phase 21a: gated first-run welcome flow
    programs: [],              // empty; user picks a starter template or builds from scratch
    activeProgramId: null,
    exercises: [...BUILTIN_EXERCISES],
    sessions: [],
    activeSessionId: null,
    bodyEntries: [],
    measurements: [],
    photos: [],
    prs: [],
    prsVersion: 3,
    prefs: {
      units: 'lb',
      restDefault: 120,
      restAutoStart: true,
      haptics: true,
      sound: false,
      theme: 'dark',
      accent: 'acid',
      notifRest: true,
      // Phase 36: progression-engine mode ('double' = advance reps then bump weight at repMax)
      progression: { mode: 'double' },
      barWeight: 45, // lb
      plates: { lb: [45, 35, 25, 10, 5, 2.5], kg: [25, 20, 15, 10, 5, 2.5, 1.25] },
    },
    coach: { clients: [], threads: {} },  // Pro feature; populated only if user becomes a coach in v3+
    // Sync tombstones: { <collection>: { <id>: deletedAt } }. Written ONLY by
    // explicit user deletes, so a delete reaches every synced device while a
    // record that merely vanishes (restore, reset) is never deleted elsewhere.
    deleted: {},
    toasts: [],
  };
}
// Untouched defaults are "never edited" for sync (they never beat a real edit).
configureSyncDefaults({ prefs: defaultState().prefs, exercises: BUILTIN_EXERCISES });

const TOMB_TTL = 400 * 86400000;
// Record explicit deletes (see state.deleted). Old tombstones are dropped.
function withTombs(deleted, coll, ids, now = Date.now()) {
  const list = (Array.isArray(ids) ? ids : [ids]).filter(id => id != null);
  if (!list.length) return deleted || {};
  const out = { ...(deleted || {}) };
  const cur = {};
  for (const [id, t] of Object.entries(out[coll] || {})) if (now - t < TOMB_TTL) cur[id] = t;
  for (const id of list) cur[String(id)] = now;
  out[coll] = cur;
  return out;
}
// A restore that brings a deleted record back un-deletes it.
function withoutTombs(deleted, coll, ids) {
  const d = deleted && deleted[coll];
  if (!d) return deleted || {};
  const drop = new Set((ids || []).map(String));
  const next = {};
  let hit = false;
  for (const [id, t] of Object.entries(d)) { if (drop.has(id)) hit = true; else next[id] = t; }
  return hit ? { ...deleted, [coll]: next } : deleted;
}
// Completion time of the latest finished workout of a program (excluding one), or 0.
function lastCompletedAt(sessions, programId, exceptId) {
  let t = 0;
  for (const x of sessions || []) if (x.programId === programId && x.completedAt && x.id !== exceptId && x.completedAt > t) t = x.completedAt;
  return t;
}

// Personal records derived from history (best e1RM per exercise on the
// effective load, working sets only — see prs.js). Recomputed after edits so a
// corrected typo stops being a "PR". `s` supplies exercises / body weights.
function computePRs(sessions, exercises, s = {}) {
  return prsFrom({ ...s, sessions, exercises }, sessions);
}

// A new program remembers its content hash, so sync can tell an untouched
// onboarding program from one the user edited (dropped on join; sync.js).
function withOrigin(p) {
  if (!isObj(p)) return p;
  const { origin: _o, ...rest } = p;
  return { ...rest, origin: { h: programContentHash(rest), at: Date.now() } };
}

// ——— persistence ———
// (storage itself lives in persist.js; this is the shape / repair layer)
const arr = (v) => Array.isArray(v) ? v : [];
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const num = (v, d = 0) => { const n = Number(v); return Number.isFinite(n) ? n : d; };

// One session into the shape every screen assumes. Returns the SAME object
// when nothing needs fixing (so a load never rewrites thousands of records).
// Repairs: numbers out of range (validate.js), set timestamps stored as
// strings, and times that are missing or not numbers (taken from the sets).
function normSession(ss) {
  let changed = false;
  let sets = ss.sets;
  if (!Array.isArray(sets)) { sets = []; changed = true; }
  else {
    let out = null;
    for (let i = 0; i < sets.length; i++) {
      const st = sets[i];
      if (!isObj(st)) { out = out || sets.slice(0, i); changed = true; continue; }
      const fixed = sanitizeSet(st);
      if (fixed !== st && !out) out = sets.slice(0, i);
      if (out) out.push(fixed);
    }
    if (out) { sets = out; changed = true; }
  }
  let startedAt = ss.startedAt, completedAt = ss.completedAt;
  const okT = (t) => typeof t === 'number' && Number.isFinite(t) && t > 0;
  if (!okT(startedAt)) {
    const ts = sets.map(st => st.ts).filter(okT);
    startedAt = toEpochMs(ss.startedAt) || (ts.length ? Math.min(...ts) : 0) || toEpochMs(ss.completedAt) || Date.now();
    changed = true;
  }
  if (completedAt != null && !okT(completedAt)) { completedAt = toEpochMs(completedAt) || null; changed = true; }
  let exercises = ss.exercises;
  if (exercises === null) { changed = true; exercises = undefined; }
  else if (exercises !== undefined) {
    if (!Array.isArray(exercises) || exercises.some(sl => !isObj(sl) || !Array.isArray(sl.rows))) {
      exercises = arr(exercises).filter(isObj).map(sl => Array.isArray(sl.rows) ? sl : { ...sl, rows: arr(sl.rows) });
      changed = true;
    }
  }
  if (!changed) return ss;
  const out = { ...ss, startedAt, completedAt: completedAt ?? null, sets };
  if (exercises === undefined) delete out.exercises; else out.exercises = exercises;
  return out;
}

// One-time repair for sessions earlier CSV imports left behind: workouts with
// sets but no completedAt that are not the live workout ("ghosts" that hid the
// history), and an import's per-exercise fragments of one workout. Ghosts are
// completed at their last set (or start + 1 h); imported fragments of one day
// are merged back into one workout.
function repairGhosts(sessions, activeSessionId) {
  let touched = false;
  const out = [];
  const byDay = new Map();
  for (const ss of sessions) {
    if (ss.completedAt || ss.id === activeSessionId) { out.push(ss); continue; }
    if (!(ss.sets || []).length) { touched = true; continue; } // empty and not live: never a real workout
    const ts = ss.sets.map(st => st.ts).filter(t => typeof t === 'number' && t > 0);
    const end = ts.length ? Math.max(...ts) : ss.startedAt + 3600e3;
    const fixed = { ...ss, completedAt: Math.max(end, ss.startedAt + 60e3), repaired: true };
    touched = true;
    if (/^sess_/.test(String(ss.id)) && !ss.programId) {
      const k = localDateStr(fixed.startedAt);
      const prev = byDay.get(k);
      if (prev) {
        const sets = [...prev.sets, ...fixed.sets.map((st, i) => ({ ...st, setIdx: (st.setIdx ?? i) }))];
        const merged = { ...prev, startedAt: Math.min(prev.startedAt, fixed.startedAt), completedAt: Math.max(prev.completedAt, fixed.completedAt), sets };
        out[out.indexOf(prev)] = merged; byDay.set(k, merged);
        continue;
      }
      byDay.set(k, fixed);
    }
    out.push(fixed);
  }
  return touched ? out : sessions;
}

// Coerce nested records into the shape every screen assumes. A restored backup,
// a sync payload or a hand-edited store with one bad record (days: null, a set
// with w: "135", sessions: null) must never crash Home on every launch.
// Valid records keep their identity; numbers are clamped to the keypad limits
// (validate.js) so a hostile import can't show "best 1568181818 lb".
function normalizeState(s) {
  if (!isObj(s)) return s;
  const programs = arr(s.programs).filter(isObj).map(p => (Array.isArray(p.days) && p.days.every(d => isObj(d) && Array.isArray(d.exercises) && d.exercises.every(isObj))) ? p : ({
    ...p,
    days: arr(p.days).filter(isObj).map(d => ({ ...d, exercises: arr(d.exercises).filter(isObj) })),
  }));
  let sessions = arr(s.sessions).filter(x => isObj(x) && x.id);
  let anyFix = sessions.length !== arr(s.sessions).length;
  sessions = sessions.map(ss => { const n = normSession(ss); if (n !== ss) anyFix = true; return n; });
  const repaired = repairGhosts(sessions, s.activeSessionId);
  if (repaired !== sessions) { sessions = repaired; anyFix = true; }
  const out = {
    ...s,
    programs,
    sessions: anyFix ? sessions : s.sessions,
    exercises: arr(s.exercises).filter(e => isObj(e) && e.id),
    bodyEntries: arr(s.bodyEntries).filter(isObj),
    measurements: arr(s.measurements).filter(isObj),
    photos: arr(s.photos).filter(isObj),
    prs: arr(s.prs).filter(isObj),
    prefs: isObj(s.prefs) ? s.prefs : {},
    user: isObj(s.user) ? s.user : {},
    coach: isObj(s.coach) ? s.coach : { clients: [], threads: {} },
    deleted: isObj(s.deleted) ? s.deleted : {},
  };
  const ids = new Set(programs.map(p => p.id));
  if (out.activeProgramId && !ids.has(out.activeProgramId)) out.activeProgramId = programs[0]?.id || null;
  if (out.activeSessionId && !out.sessions.some(x => x.id === out.activeSessionId && !x.completedAt)) out.activeSessionId = null;
  return out;
}

// Ask the browser not to evict our storage (iOS/Safari evicts non-persistent
// origins under pressure). Asked once the data is in IndexedDB, and again on
// the first workout. Idempotent.
let _persistAsked = false;
function askPersist() {
  if (_persistAsked) return; _persistAsked = true;
  try {
    if (navigator.storage && navigator.storage.persist) {
      (navigator.storage.persisted ? navigator.storage.persisted() : Promise.resolve(false))
        .then(p => (p ? true : navigator.storage.persist())).catch(() => {});
    }
  } catch (_) {}
}

// Set when the stored state could not be parsed: the raw text is copied to a
// side key first so nothing is lost, and the app shows a recovery notice once.
let corruptBackupKey = null;
function takeCorruptBackupKey() { const k = corruptBackupKey; corruptBackupKey = null; return k; }

// The boot snapshot (persist.bootRead) the first render came from; hydrate()
// reconciles it with IndexedDB.
let _boot = null;
let hydrateStarted = false;

// Defaults merged in, built-in metadata refreshed, PRs rebuilt when their
// rules changed (only on complete data), ephemeral fields reset.
function completeShape(s, { complete = true } = {}) {
  const def = defaultState();
  return {
    ...def,
    ...s,
    // Built-in exercises gain metadata over time (e.g. secondary muscles);
    // refresh those fields on the persisted copies without touching user edits.
    exercises: (s.exercises.length ? s.exercises : def.exercises).map(e => {
      const b = BUILTIN_BY_ID[e.id];
      return b && !e.secondary ? { ...e, secondary: b.secondary } : e;
    }),
    prefs: { ...def.prefs, ...(s.prefs || {}) },
    user: { ...def.user, ...(s.user || {}) },
    coach: { ...def.coach, ...(s.coach || {}) },
    // PRs follow prs.js (effective load, no mini-sets / holds / cardio): rebuild once from history.
    prs: s.prsVersion === PRS_VERSION || !complete ? (s.prs || []) : computePRs(s.sessions, s.exercises, s),
    prsVersion: s.prsVersion === PRS_VERSION || !complete ? (s.prsVersion ?? PRS_VERSION) : PRS_VERSION,
    // toasts are ephemeral UI state — never restore them. A persisted toast has
    // no dismissal timer after a reload, so it would stick on screen forever.
    toasts: [],
  };
}

// The live workout saved on every change (persist LIVE_KEY) is newer than any
// snapshot: put it back unless that workout was finished meanwhile.
function overlayLive(s, live) {
  const ses = live && live.session;
  if (!isObj(ses) || !ses.id || ses.completedAt) return s;
  const cur = s.sessions.find(x => x.id === ses.id);
  if (cur && cur.completedAt) return s;
  if (s.activeSessionId && s.activeSessionId !== ses.id && s.sessions.some(x => x.id === s.activeSessionId && !x.completedAt)) return s;
  const fixed = normSession(ses);
  return { ...s, activeSessionId: ses.id, sessions: cur ? s.sessions.map(x => x.id === ses.id ? fixed : x) : [...s.sessions, fixed] };
}

function loadState() {
  const b = persist.bootRead();
  _boot = b;
  if (b.source === 'corrupt') {
    console.warn('liftlog: stored state unreadable, using defaults');
    // Keep the unreadable data instead of silently overwriting it on next save.
    const key = LS_KEY + '.corrupt-' + Date.now();
    try { localStorage.setItem(key, b.raw); corruptBackupKey = key; } catch (_) { corruptBackupKey = 'unsaved'; }
    try { localStorage.removeItem(LS_KEY); localStorage.removeItem(persist.SIG_KEY); } catch (_) {}
    return { ...defaultState(), hydrated: false };
  }
  if (!b.data) return { ...defaultState(), hydrated: false };
  try {
    const s = normalizeState(b.data);
    if (!isObj(s)) throw new Error('stored state is not an object');
    const shaped = completeShape(s, { complete: !b.partial });
    return { ...overlayLive(shaped, b.live), hydrated: false };
  } catch (e) {
    console.warn('liftlog: loadState failed, using defaults', e);
    return { ...defaultState(), hydrated: false };
  }
}

// After hydrate: IndexedDB held more / newer data than the boot snapshot.
// Fields the user changed since the first render keep the in-memory value;
// everything else comes from `loaded`. preferBoot: the snapshot was newer.
function mergeLoaded(prev, loaded, boot, preferBoot) {
  const L = normalizeState(loaded);
  const out = { ...L };
  for (const k of Object.keys(prev)) {
    if (k === 'sessions' || k === 'toasts' || k === 'hydrated') continue;
    if (preferBoot || !(k in L) || prev[k] !== boot[k]) out[k] = prev[k];
  }
  const bootById = new Map((boot.sessions || []).map(x => [x.id, x]));
  const prevIds = new Set(prev.sessions.map(x => x.id));
  const byId = new Map(L.sessions.map(x => [x.id, x]));
  for (const x of prev.sessions) if (preferBoot || bootById.get(x.id) !== x) byId.set(x.id, x); // new or edited since boot
  for (const id of bootById.keys()) if (!prevIds.has(id)) byId.delete(id);                // deleted since boot
  const tomb = (out.deleted && out.deleted.sessions) || {};
  const sessions = [...byId.values()].filter(x => tomb[x.id] == null || !x.completedAt).sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0));
  return completeShape({ ...out, sessions, prsVersion: L.prsVersion });
}

// Rescue copy of the in-memory state (storage full, crash screen): a normal
// Lift Log backup, restorable with Settings → Import JSON.
let _latest = null; // latest committed state (module scope: reachable from the crash screen)
function backupJSONOf(s) {
  const st = s || _latest || {};
  return JSON.stringify({
    schemaVersion: 1, app: 'liftlog', exportedAt: new Date().toISOString(),
    sessions: arr(st.sessions), prs: arr(st.prs), programs: arr(st.programs), exercises: arr(st.exercises),
    bodyEntries: arr(st.bodyEntries), measurements: arr(st.measurements), prefs: st.prefs || {},
    activeProgramId: st.activeProgramId ?? null, activeSessionId: st.activeSessionId ?? null,
    profile: st.profile ?? null, welcomed: !!st.welcomed,
    user: { pro: !!(st.user && st.user.pro), name: (st.user && st.user.name) || '' },
    times: (() => { try { return recordTimes(); } catch (_) { return undefined; } })(),
  });
}
function rescueBackupJSON() { return _latest ? backupJSONOf(_latest) : null; }

// ——— Context ———
const AppCtx = React.createContext(null);
// Toast action callbacks live outside state (functions aren't persistable).
const toastActions = new Map();

// Fallback parent for goBack when the in-memory back-stack is empty — happens
// whenever the route was RESTORED from localStorage (page reload, PWA relaunch,
// service-worker update) rather than navigated to, since the stack is in-memory
// only. Without this, Back on a restored deep screen (e.g. program-edit) is a
// silent no-op and traps the user. Maps each deep route to its logical parent.
function parentRouteOf(name) {
  if (!name) return 'home';
  const PARENTS = {
    'program-edit': 'programs',
    'exercise-edit': 'library',
    'exercise': 'library',
    'ai-coach': 'home',
    'subscription': 'settings',
    'subscription-cancel': 'subscription',
    'active-workout': 'home',
    'workout-summary': 'home',
    'form-record': 'home',
    'form-check': 'home',
    'ai-coach': 'home',
    'analytics': 'home',
    'paywall': 'home',
    'client-detail': 'coach',
    'client-messages': 'coach',
    'client-assign': 'coach',
  };
  if (PARENTS[name]) return PARENTS[name];
  if (name.startsWith('settings/')) return 'settings';
  return 'home';
}

function AppProvider({ children }) {
  const [state, setState] = React.useState(loadState);
  const stateRef = React.useRef(state);
  stateRef.current = state;
  // route is stored outside main state so navigation doesn't churn it
  // Phase 21a: default route is 'welcome' (no auth-splash). Actual render
  // chooses welcome vs home based on state.welcomed + programs length.
  const [route, setRouteRaw] = React.useState(() => {
    try {
      const r = localStorage.getItem(ROUTE_KEY);
      if (r) {
        const parsed = JSON.parse(r);
        // Phase 21a: rewrite obsolete auth/onboard routes to a safe default
        if (parsed && /^(auth-|onboard-)/.test(parsed.name || '')) {
          return { name: 'home', params: {} };
        }
        return parsed;
      }
      return { name: 'home', params: {} };
    } catch { return { name: 'home', params: {} }; }
  });

  // ——— Persistence (persist.js): IndexedDB + boot mirror + live-workout key ———
  // toastRef lets effects reach toast() without recreating them on every render.
  const toastRef = React.useRef(null);
  // Storage full: the in-memory state is ahead of disk. Shell shows a blocking
  // notice (download a backup of what is in memory / free space).
  const [storageFull, setStorageFull] = React.useState(false);
  const [corruptKey] = React.useState(() => takeCorruptBackupKey());
  const pendingAdopt = React.useRef([]);
  const pendingFinish = React.useRef(null);
  const bootRef = React.useRef(null);
  if (!bootRef.current) bootRef.current = { boot: _boot, state };
  // Without a usable snapshot but with data in IndexedDB, render nothing until it is loaded.
  const waitForData = bootRef.current.boot && bootRef.current.boot.source === 'idb';
  const persistRef = React.useRef(null);
  if (!persistRef.current) {
    persistRef.current = persist.createPersister({
      getState: () => stateRef.current,
      onStorageFull: (on) => setStorageFull(on),
      onRemote: (patch) => {
        // Another tab saved: apply its records; they are on disk already (adopted after commit).
        pendingAdopt.current.push(patch);
        setState(prev => {
          let next = prev;
          if (patch.doc) {
            const d = normalizeState({ ...patch.doc, sessions: prev.sessions });
            next = { ...next, ...d, sessions: next.sessions, toasts: prev.toasts, hydrated: prev.hydrated };
          }
          if (patch.put.length || patch.del.length || patch.hasLive) {
            const byId = new Map(next.sessions.map(x => [x.id, x]));
            for (const x of patch.put) byId.set(x.id, normSession(x));
            for (const id of patch.del) byId.delete(id);
            if (patch.hasLive) {
              for (const [id, x] of byId) if (!x.completedAt && (!patch.live || id !== patch.live.id)) byId.delete(id);
              if (patch.live) byId.set(patch.live.id, normSession(patch.live));
            }
            next = { ...next, sessions: [...byId.values()].sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0)) };
            if (patch.hasLive) next.activeSessionId = patch.live ? patch.live.id : (patch.doc ? next.activeSessionId : null);
          }
          return next;
        });
      },
    });
  }
  React.useEffect(() => {
    _latest = state;
    const p = persistRef.current;
    if (pendingAdopt.current.length) { for (const patch of pendingAdopt.current.splice(0)) p.adopt(patch, state); }
    if (pendingFinish.current) { const f = pendingFinish.current; pendingFinish.current = null; f(state); return; }
    p.schedule(state);
  }, [state]);
  React.useEffect(() => {
    try { localStorage.setItem(ROUTE_KEY, JSON.stringify(route)); } catch (_) { /* quiet */ }
  }, [route]);

  // Hydrate once (StrictMode runs effects twice in development).
  React.useEffect(() => {
    if (hydrateStarted) return;
    hydrateStarted = true;
    const { boot, state: bootState } = bootRef.current;
    persistRef.current.hydrate(boot || { source: 'none' }, stateRef.current)
      .then((res) => {
        if (res && res.replace) {
          setState(prev => {
            const merged = mergeLoaded(prev, res.replace, bootState, res.mirrorNewer);
            return { ...overlayLive(merged, persist.readLiveKey()), toasts: prev.toasts, hydrated: true };
          });
          pendingFinish.current = res.finish;
        } else setState(prev => (prev.hydrated ? prev : { ...prev, hydrated: true }));
        if (res && res.migrated) console.info('[liftlog] moved your data to IndexedDB (a copy of the old storage is kept)');
        persist.expirePreRestore().catch(() => {});
        // The pre-IndexedDB undo copy sat in localStorage (and ate half the quota): move it.
        try {
          const old = localStorage.getItem(LS_KEY + '.pre-restore');
          if (old) { persist.savePreRestore(JSON.parse(old)).finally(() => { try { localStorage.removeItem(LS_KEY + '.pre-restore'); } catch (_) {} }); }
        } catch (_) { try { localStorage.removeItem(LS_KEY + '.pre-restore'); } catch (_) {} }
        if ((stateRef.current.sessions || []).some(x => x.completedAt)) askPersist();
      })
      .catch((e) => {
        console.warn('[liftlog] loading saved data failed', e);
        persistRef.current.markHydrated(stateRef.current);
        setState(prev => ({ ...prev, hydrated: true }));
      });
  }, []);

  // Test / dev seam (persist.js): read the live state, seed any amount of data.
  React.useEffect(() => {
    window.__liftlog = {
      getState: () => JSON.parse(JSON.stringify(persist.persistable(stateRef.current))),
      hydrated: () => !!stateRef.current.hydrated,
      flush: () => persistRef.current.flush(),
      seed: async (s) => { persistRef.current.freeze(); await persist.seedState(normalizeState(s)); },
      backupJSON: () => backupJSONOf(stateRef.current),
      get actions() { return actionsRef.current; },
    };
  }, []);
  const actionsRef = React.useRef(null);


  // Back stack, mirrored into browser history so the Android/browser Back button
  // (and the iOS edge-swipe in standalone mode) walks back through screens.
  // Kept in refs (not inside setState updaters) so StrictMode's double-invoked
  // updaters can't double-push.
  const stackRef = React.useRef([]);
  const routeRef = React.useRef(route);
  routeRef.current = route;

  const setRoute = React.useCallback((name, params = {}) => {
    const prev = routeRef.current;
    if (prev && prev.name === name && JSON.stringify(prev.params || {}) === JSON.stringify(params || {})) return;
    stackRef.current.push(prev);
    if (stackRef.current.length > 40) stackRef.current.shift();
    try { window.history.pushState({ ll: stackRef.current.length }, ''); } catch (_) {}
    routeRef.current = { name, params };
    setRouteRaw({ name, params });
  }, []);

  const popRoute = React.useCallback(() => {
    const prevRoute = stackRef.current.pop();
    // Empty stack (route restored from localStorage after a reload / PWA
    // relaunch / SW update) → fall back to a sensible parent instead of
    // trapping the user on a deep screen with a dead Back button.
    const next = prevRoute || { name: parentRouteOf(routeRef.current?.name), params: {} };
    routeRef.current = next;
    setRouteRaw(next);
  }, []);

  const goBack = React.useCallback(() => {
    if (stackRef.current.length > 0) {
      try { window.history.back(); return; } catch (_) { /* fall through */ }
    }
    popRoute();
  }, [popRoute]);

  React.useEffect(() => {
    // An open sheet owns the top history entry: Back closes it first.
    const onPop = (e) => { if (handleSheetPop(e)) return; popRoute(); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [popRoute]);

  const replaceRoute = React.useCallback((name, params = {}) => {
    routeRef.current = { name, params };
    setRouteRaw({ name, params });
  }, []);

  // Toast helper
  // At most two toasts at once; identical text is de-duplicated and PR toasts
  // coalesce into one ("3 new PRs · latest …") instead of stacking up.
  // opts.action = { label, run } renders a tappable action (e.g. Undo).
  const toast = React.useCallback((text, tone = 'default', opts = {}) => {
    const id = uid('t_');
    setState(s => {
      let list = s.toasts.filter(t => t.text !== text);
      let entry = { id, text, tone };
      if (tone === 'pr') {
        const prev = list.filter(t => t.tone === 'pr');
        if (prev.length) {
          const count = prev.reduce((n, t) => n + (t.count || 1), 0) + 1;
          entry = { id, tone, count, text: `${count} new PRs · ${String(text).replace(/^New PR · /, '')}` };
          list = list.filter(t => t.tone !== 'pr');
        }
      }
      if (opts.action) { toastActions.set(id, opts.action); entry.action = opts.action.label; }
      return { ...s, toasts: [...list, entry].slice(-2) };
    });
    setTimeout(() => {
      toastActions.delete(id);
      setState(s => ({ ...s, toasts: s.toasts.filter(t => t.id !== id) }));
    }, opts.action ? 5000 : tone === 'pr' ? 3400 : 2800);
  }, []);
  const runToastAction = React.useCallback((id) => {
    const a = toastActions.get(id);
    toastActions.delete(id);
    setState(s => ({ ...s, toasts: s.toasts.filter(t => t.id !== id) }));
    if (a) { try { a.run(); } catch (e) { console.warn('toast action failed', e); } }
  }, []);
  // Expose toast to the quota-aware save effect (avoids recreating the effect on every render).
  React.useEffect(() => { toastRef.current = toast; }, [toast]);

  // ——— Reducer-style mutations ———
  const actions = React.useMemo(() => ({
    // ——— Welcome / identity (Phase 21a: no accounts, optional display name)
    setUserName: (name) => {
      setState(s => ({ ...s, user: { ...s.user, name: (name || '').trim() } }));
    },
    // Mark the current week as a deload for a program (or clear it with on=false).
    setDeloadWeek: (programId, on = true) => {
      setState(s => ({ ...s, programs: s.programs.map(p => p.id !== programId ? p : { ...p, deloadWeek: on ? weekStart(Date.now()) : null }) }));
      toast(on ? 'Deload week on — lighter sessions until Sunday' : 'Deload week off');
    },
    // Training profile from onboarding: { goal, experience, daysPerWeek, sessionMinutes,
    // equipment: string[], injuries, sex?, bodyweight? } — feeds program generation and the AI coach.
    setProfile: (patch) => setState(s => ({ ...s, profile: { ...(s.profile || {}), ...patch } })),
    markWelcomed: () => {
      setState(s => ({ ...s, welcomed: true }));
    },
    pickStarterProgram: (starterId) => {
      // Called from WelcomeScreen; starterId matches STARTER_PROGRAMS[i].id
      const starter = STARTER_PROGRAMS.find(t => t.id === starterId);
      if (!starter) { toast('Template not found'); return; }
      const built = starter.build();
      setState(s => ({
        ...s,
        programs: [...s.programs, withOrigin(built)],
        activeProgramId: built.id,
        welcomed: true,
      }));
      toast(`${starter.label} added`);
    },

    // ——— Pro
    startTrial: () => {
      const end = Date.now() + 7 * 24 * 60 * 60 * 1000;
      setState(s => ({ ...s, user: { ...s.user, pro: true, trialEndsAt: end }}));
      toast('7-day Pro trial started');
    },
    upgradeToPro: () => {
      setState(s => ({ ...s, user: { ...s.user, pro: true, trialEndsAt: null }}));
      toast('Welcome to Pro');
    },
    cancelPro: (reason) => {
      setState(s => ({ ...s, user: { ...s.user, pro: false, trialEndsAt: null }}));
      toast('Subscription cancelled');
    },

    // ——— Programs
    addProgram: (prog, opts = {}) => {
      const p = withOrigin({ ...prog, id: uid('prog_') });
      setState(s => ({ ...s, programs: [...s.programs, p], activeProgramId: p.id }));
      if (!opts.silent) toast('Program created');
      return p.id;
    },
    updateProgram: (id, patch) => {
      setState(s => ({ ...s, programs: s.programs.map(p => p.id === id ? { ...p, ...patch } : p) }));
    },
    deleteProgram: (id) => {
      setState(s => {
        const progs = s.programs.filter(p => p.id !== id);
        return { ...s, programs: progs, deleted: withTombs(s.deleted, 'programs', id), activeProgramId: progs.length ? (s.activeProgramId === id ? progs[0].id : s.activeProgramId) : null };
      });
      toast('Program deleted');
    },
    duplicateProgram: (id) => {
      setState(s => {
        const src = s.programs.find(p => p.id === id); if (!src) return s;
        const copy = JSON.parse(JSON.stringify(src));
        copy.id = uid('prog_');
        copy.name = `${src.name} (copy)`;
        copy.days = copy.days.map(d => ({ ...d, id: uid('day_'), exercises: d.exercises.map(e => ({ ...e, id: uid('pe_') })) }));
        return { ...s, programs: [...s.programs, copy] };
      });
      toast('Program duplicated');
    },
    setActiveProgram: (id) => setState(s => ({ ...s, activeProgramId: id })),

    addDay: (programId, dayName = 'New Day') => {
      setState(s => ({ ...s, programs: s.programs.map(p => p.id !== programId ? p : {
        ...p, days: [...p.days, { id: uid('day_'), name: dayName, day: '', exercises: [] }]
      }) }));
    },
    updateDay: (programId, dayId, patch) => {
      setState(s => ({ ...s, programs: s.programs.map(p => p.id !== programId ? p : {
        ...p, days: p.days.map(d => d.id !== dayId ? d : { ...d, ...patch })
      }) }));
    },
    deleteDay: (programId, dayId) => {
      setState(s => ({ ...s, programs: s.programs.map(p => p.id !== programId ? p : {
        ...p, days: p.days.filter(d => d.id !== dayId)
      }) }));
    },
    addExerciseToDay: (programId, dayId, exerciseId) => {
      setState(s => {
        const ex = s.exercises.find(e => e.id === exerciseId); if (!ex) return s;
        const pe = {
          id: uid('pe_'),
          exerciseId,
          name: ex.name,
          group: ex.group,
          unit: ex.unit || 'reps',
          sets: 3,
          repMin: 8, repMax: 10,
          targetWeight: 0,
          rest: 120,
          yt: ex.yt ?? '',
          alternates: [],
        };
        return { ...s, programs: s.programs.map(p => p.id !== programId ? p : {
          ...p, days: p.days.map(d => d.id !== dayId ? d : { ...d, exercises: [...d.exercises, pe] })
        }) };
      });
    },
    updateExerciseInDay: (programId, dayId, peId, patch) => {
      setState(s => ({ ...s, programs: s.programs.map(p => p.id !== programId ? p : {
        ...p, days: p.days.map(d => d.id !== dayId ? d : {
          ...d, exercises: d.exercises.map(e => e.id !== peId ? e : { ...e, ...patch })
        })
      }) }));
    },
    // alternates: Array<{ name: string, yt: string }>, max 2.
    // Called by Phase 34 importer and by tests; NEVER called by the swap UI
    // (swap is session-only and must not mutate pe.alternates).
    setAlternates: (programId, dayId, peId, alternates) => {
      // Guard: always persist a valid array, capped at 2 (sheet provides ≤2 subs).
      const safe = (Array.isArray(alternates) ? alternates : []).slice(0, 2);
      setState(s => ({ ...s, programs: s.programs.map(p => p.id !== programId ? p : {
        ...p, days: p.days.map(d => d.id !== dayId ? d : {
          ...d, exercises: d.exercises.map(e => e.id !== peId ? e : { ...e, alternates: safe })
        })
      }) }));
    },
    // Sets the active week index (0-based) on program.periodization.currentWeek.
    // CRITICAL: writes ONLY to periodization.currentWeek — never to the top-level
    // p.currentWeek (1-based "week N of M" counter used by ProgramEditor stepper).
    setCurrentWeek: (programId, weekIndex) => {
      setState(s => ({
        ...s,
        programs: s.programs.map(p => p.id !== programId ? p : {
          ...p,
          periodization: {
            ...(p.periodization || { weeks: [] }),
            currentWeek: weekIndex,
          },
        }),
      }));
    },

    // ——— Phase 34: importProgram — ADDITIVE transactional program import.
    // pkg = { kind:'program-package', app:'liftlog', schemaVersion:1, program:{...} }
    // Generates fresh UIDs for prog/day/PE to avoid ID collisions;
    // remaps periodization.weeks[].prescriptions keys via peIdMap; sets new program active.
    // Pattern: mirrors importSessions (store.jsx:804) — single setState call.
    // Returns the new program id so ImportJSON can confirm/toast.
    importProgram: (pkg) => {
      if (!pkg || pkg.kind !== 'program-package' || pkg.app !== 'liftlog') return;
      const srcProg = pkg.program;
      if (!srcProg || !Array.isArray(srcProg.days)) return;

      // Build old-peId → new-peId mapping BEFORE periodization remap (LOAD-BEARING — Pitfall 7)
      const peIdMap = {};

      const newDays = srcProg.days.map(day => ({
        ...day,
        id: uid('day_'),
        exercises: (day.exercises || []).map(pe => {
          const newPeId = uid('pe_');
          peIdMap[pe.id] = newPeId;
          return { ...pe, id: newPeId };
        }),
      }));

      const srcP = srcProg.periodization;
      const newPeriodization = srcP ? {
        currentWeek: srcP.currentWeek ?? 0,
        weeks: (srcP.weeks || []).map(wk => ({
          ...wk,
          prescriptions: Object.fromEntries(
            Object.entries(wk.prescriptions || {})
              .filter(([oldId]) => peIdMap[oldId] != null) // WR-04: drop orphaned keys
              .map(([oldId, rx]) => [peIdMap[oldId], rx])
          ),
        })),
      } : undefined;

      const newProg = {
        ...srcProg,
        id: uid('prog_'),
        days: newDays,
        ...(newPeriodization ? { periodization: newPeriodization } : {}),
      };

      setState(prev => ({
        ...prev,
        programs: [...(prev.programs || []), newProg],
        activeProgramId: newProg.id,
      }));

      return newProg.id;
    },

    // ——— Phase 35: importLoggedSessions — ADDITIVE transactional logged-session import.
    // Mirrors importSessions (store.jsx:858) for the dedup body; mirrors importProgram for the
    // outer wrapper/return shape.
    // snapshot = { kind:'sessions-package', app:'liftlog', schemaVersion:1,
    //              sessions:[...], exercisesToCreate:[...], ambiguities:[...] }
    // Resolves programId/dayId from prev.programs at import time by stable nip_* exerciseId
    // (PITFALL-1: never join by pe.id — those are remapped to fresh UIDs at importProgram time).
    // Returns { sessionsAdded, setsAdded, exercisesAdded, duplicatesSkipped }.
    // The caller (ImportJSON) applies per-DB toggles to w BEFORE calling this action;
    // the store receives already-patched w values and does NOT read ambiguities.
    importLoggedSessions: (snapshot) => {
      if (!snapshot || snapshot.kind !== 'sessions-package' || snapshot.app !== 'liftlog') return null;
      const rawSessions  = Array.isArray(snapshot.sessions)          ? snapshot.sessions          : [];
      const newExercises = Array.isArray(snapshot.exercisesToCreate) ? snapshot.exercisesToCreate : [];

      // WR-01: capture result via overwrite (not accumulation) so React StrictMode's
      // double-invocation of the updater produces the same value on both calls (same prev
      // → same uniqueSessions/uniqueExercises) rather than doubling the counters.
      const captured = { result: null };

      setState(prev => {
        // 1. Find the Nippard program by looking for nip_* exerciseIds in any program's days
        const nippardProg = prev.programs.find(p =>
          p.days?.some(d => d.exercises?.some(pe => pe.exerciseId?.startsWith('nip_')))
        );
        // 2. Build dayName → dayId map (falls back to empty obj if program not yet imported)
        const dayNameToId = nippardProg
          ? Object.fromEntries(nippardProg.days.map(d => [d.name, d.id]))
          : {};
        // 3. Patch sessions with resolved programId/dayId (WR-06: filter null elements)
        const sessions = rawSessions
          .filter(s => s != null && typeof s === 'object')
          .map(s => ({
            ...s,
            programId: nippardProg?.id ?? null,
            dayId:     dayNameToId[s.dayName] ?? null,
          }));

        // 4. Dedup + commit — verbatim from importSessions (store.jsx:862-898)
        const existingIds = new Set((prev.sessions || []).map(s => s.id));
        const existingFp  = new Set((prev.sessions || []).map(s => {
          const d       = s.startedAt ? localDateStr(s.startedAt) : '';
          const firstEx = s.sets && s.sets[0] ? s.sets[0].exerciseId : '';
          return `${d}|${firstEx}`;
        }));
        const uniqueSessions = [];
        for (const s of sessions) {
          if (existingIds.has(s.id)) { continue; }
          const d       = s.startedAt ? localDateStr(s.startedAt) : '';
          const firstEx = s.sets && s.sets[0] ? s.sets[0].exerciseId : '';
          const fp      = `${d}|${firstEx}`;
          if (existingFp.has(fp)) { continue; }
          uniqueSessions.push(s);
          existingIds.add(s.id);
          existingFp.add(fp);
        }
        // Only add truly new exercises (id + name dedup)
        const existingExIds   = new Set((prev.exercises || []).map(e => e.id));
        const existingExNames = new Set((prev.exercises || []).map(e => String(e.name || '').toLowerCase()));
        const uniqueExercises = [];
        for (const ex of newExercises) {
          if (existingExIds.has(ex.id) || existingExNames.has(String(ex.name || '').toLowerCase())) continue;
          uniqueExercises.push(ex);
        }
        // Assign (not accumulate) so double-invocation under StrictMode is idempotent
        captured.result = {
          sessionsAdded:     uniqueSessions.length,
          setsAdded:         uniqueSessions.reduce((acc, s) => acc + (s.sets || []).length, 0),
          exercisesAdded:    uniqueExercises.length,
          duplicatesSkipped: sessions.length - uniqueSessions.length,
        };
        return {
          ...prev,
          sessions:  [...(prev.sessions  || []), ...uniqueSessions],
          exercises: [...(prev.exercises || []), ...uniqueExercises],
        };
      });
      return captured.result;
    },

    removeExerciseFromDay: (programId, dayId, peId) => {
      setState(s => ({ ...s, programs: s.programs.map(p => p.id !== programId ? p : {
        ...p, days: p.days.map(d => d.id !== dayId ? d : { ...d, exercises: d.exercises.filter(e => e.id !== peId) })
      }) }));
    },
    reorderExercises: (programId, dayId, fromIdx, toIdx) => {
      setState(s => ({ ...s, programs: s.programs.map(p => p.id !== programId ? p : {
        ...p, days: p.days.map(d => {
          if (d.id !== dayId) return d;
          const ex = [...d.exercises];
          const [m] = ex.splice(fromIdx, 1);
          ex.splice(toIdx, 0, m);
          return { ...d, exercises: ex };
        })
      }) }));
    },
    // Mid-workout "add / remove a set" — persists a new working-set count for one
    // program-exercise so it's remembered next time. The pure withSetCount helper
    // (utils.jsx) writes to the right place: pe.sets inline for non-periodized
    // programs, or the active + all future weeks' prescriptions for periodized
    // ones (past weeks untouched). Clamped to [MIN_SETS, MAX_SETS].
    setExerciseSetCount: (programId, dayId, peId, count) => {
      setState(s => ({ ...s, programs: s.programs.map(p => p.id !== programId ? p : withSetCount(p, dayId, peId, count)) }));
    },

    // ——— Exercise library
    // Copies catalog entries (exercise_catalog.js catalogToExercise shape) into
    // the user's library so programs/history can reference them by id. Idempotent.
    ensureExercises: (list) => {
      const items = (Array.isArray(list) ? list : [list]).filter(e => e && e.id);
      if (!items.length) return;
      setState(s => {
        const have = new Set(s.exercises.map(e => e.id));
        const add = items.filter(e => !have.has(e.id));
        return add.length ? { ...s, exercises: [...s.exercises, ...add] } : s;
      });
    },
    addCustomExercise: (ex) => {
      const e = { ...ex, id: uid('xc_'), custom: true };
      setState(s => ({ ...s, exercises: [...s.exercises, e] }));
      toast('Exercise added to library');
      return e.id;
    },
    updateExercise: (id, patch) => {
      setState(s => ({ ...s, exercises: s.exercises.map(e => e.id === id ? { ...e, ...patch } : e) }));
    },
    deleteExercise: (id) => {
      setState(s => ({ ...s, exercises: s.exercises.filter(e => e.id !== id), deleted: withTombs(s.deleted, 'exercises', id) }));
      toast('Exercise removed');
    },

    // ——— Sessions (active workout logging)
    startSession: (programId, dayId) => {
      askPersist();
      // captured closure: lets us read the resolved session id out of the setState updater.
      // StrictMode-safe: both double-invocations assign the same value, second result is discarded.
      const newId = uid('ses_');
      const captured = { returnId: newId };

      setState(s => {
        // NAV-03: refuse to create a second live session that would orphan an
        // active one on a different day. Same-day resume falls through to the
        // existing dedup below (SESS-03 preserved).
        if (wouldOrphanActiveSession({ sessions: s.sessions, activeSessionId: s.activeSessionId, dayId })) {
          captured.returnId = null;
          return s; // no state mutation — live session stays intact
        }

        const todayStr = localDateStr(Date.now());
        const existing = s.sessions.find(ss =>
          ss.programId === programId &&
          ss.dayId === dayId &&
          localDateStr(ss.startedAt) === todayStr
        );
        if (existing) {
          captured.returnId = existing.id;
          if (!existing.completedAt) {
            // Resume in-progress same-day session (SESS-03)
            return { ...s, activeSessionId: existing.id };
          }
          // Already completed today — no-op; routing to review wired in Phase 42 (SESS-01)
          return s;
        }
        const prog = s.programs.find(p => p.id === programId);
        // Progression engine (progression.js): cycle-based programs know their week.
        const wk = prog ? programWeekInfo({ program: prog, sessions: s.sessions }) : null;
        const weekIndex = prog?.cycle ? wk.index
          : (prog?.periodization?.weeks?.length > 0 ? (prog.periodization.currentWeek ?? 0) : null);
        const weekLabel = prog?.cycle ? wk.label
          : (weekIndex != null ? (prog.periodization.weeks[weekIndex]?.label ?? null) : null);
        captured.returnId = newId;
        const day = prog?.days.find(d => d.id === dayId);
        // Deload week: ~40% fewer working sets and ~10% lighter (see setDeloadWeek).
        const deload = !!(prog?.deloadWeek && prog.deloadWeek === weekStart(Date.now()));
        return {
          ...s,
          activeSessionId: newId,
          sessions: [...s.sessions, {
            id: newId, programId, dayId,
            name: day?.name || 'Workout',
            startedAt: Date.now(),
            completedAt: null,
            sets: [],
            notes: '',
            weekIndex,   // null for non-periodized; undefined on pre-existing sessions (back-compat)
            weekLabel,   // null for non-periodized
            // The session owns its exercise list (see session_model.js) so the
            // workout can be edited without touching the program.
            exercises: day ? day.exercises.map(pe => {
              let sl = slotFromProgramExercise(prog, pe);
              // Percent / wave / RPE schemes: the engine prescribes this week's rows.
              if (schemeOf(pe) !== 'double') {
                const rx = prescribe({ program: prog, pe, weekIndex: wk ? wk.index : 0, units: s.prefs.units, history: s.sessions, prefs: s.prefs, exercises: s.exercises });
                if (rx && rx.rows && rx.rows.length) {
                  sl = { ...sl, scheme: schemeOf(pe), rows: rx.rows.map(r => ({ type: 'normal', ...r })), notes: sl.notes || rx.note,
                    repMin: Math.min(...rx.rows.map(r => r.reps)), repMax: Math.max(...rx.rows.map(r => r.repsMax || r.reps)) };
                }
              }
              return deload ? { ...sl, rows: sl.rows.slice(0, Math.max(1, Math.ceil(sl.rows.length * 0.6))) } : sl;
            }) : [],
            deload,
            // Informational: the engine already lightens its own deload weeks.
            planDeload: !!wk?.deload,
          }],
        };
      });

      // Return captured.returnId — NOT newId: after dedup, newId may be the unused pre-generated id
      return captured.returnId;
    },
    // Freestyle workout: no program day, exercises added as you go.
    // Returns the new session id, or null if another workout is in progress.
    startFreestyle: (name = 'Workout') => {
      askPersist();
      const newId = uid('ses_');
      const captured = { id: newId };
      setState(s => {
        const live = s.sessions.find(x => x.id === s.activeSessionId && !x.completedAt);
        if (live) { captured.id = null; return s; }
        return {
          ...s,
          activeSessionId: newId,
          sessions: [...s.sessions, {
            id: newId, programId: null, dayId: null, name,
            startedAt: Date.now(), completedAt: null, sets: [], notes: '',
            weekIndex: null, weekLabel: null, exercises: [],
          }],
        };
      });
      return captured.id;
    },
    // Structural edits to a session's exercise list. `updater(slots) → slots`.
    // Sets logged against slots that no longer exist are dropped.
    updateSessionSlots: (sessionId, updater) => {
      setState(s => ({
        ...s,
        sessions: s.sessions.map(ss => {
          if (ss.id !== sessionId) return ss;
          const next = updater(sessionSlots(ss, s.programs));
          const keep = new Set(next.map(x => x.id));
          return { ...ss, exercises: next, sets: ss.sets.filter(st => keep.has(st.peId)) };
        }),
      }));
    },
    // Delete one row (and its logged set, if any) from a slot; later rows shift up.
    deleteSessionRow: (sessionId, peId, rowIdx) => {
      setState(s => ({
        ...s,
        sessions: s.sessions.map(ss => {
          if (ss.id !== sessionId) return ss;
          const slots = sessionSlots(ss, s.programs).map(sl => sl.id !== peId ? sl : { ...sl, rows: sl.rows.filter((_, i) => i !== rowIdx) });
          const sets = ss.sets
            .filter(st => !(st.peId === peId && st.setIdx === rowIdx))
            .map(st => st.peId === peId && st.setIdx > rowIdx ? { ...st, setIdx: st.setIdx - 1 } : st);
          return { ...ss, exercises: slots, sets };
        }),
      }));
    },
    // Insert planned rows (e.g. warm-ups) into a slot at `atIdx`; logged sets at or
    // after that index shift down so they stay attached to their rows.
    insertSessionRows: (sessionId, peId, atIdx, rows) => {
      if (!rows || !rows.length) return;
      setState(s => ({
        ...s,
        sessions: s.sessions.map(ss => {
          if (ss.id !== sessionId) return ss;
          const slots = sessionSlots(ss, s.programs).map(sl => sl.id !== peId ? sl : {
            ...sl, rows: [...sl.rows.slice(0, atIdx), ...rows, ...sl.rows.slice(atIdx)],
          });
          const sets = ss.sets.map(st => st.peId === peId && st.setIdx >= atIdx ? { ...st, setIdx: st.setIdx + rows.length } : st);
          return { ...ss, exercises: slots, sets };
        }),
      }));
    },
    unlogSet: (sessionId, peId, setIdx) => {
      setState(s => ({ ...s, sessions: s.sessions.map(ss => ss.id !== sessionId ? ss : {
        ...ss, sets: ss.sets.filter(x => !(x.peId === peId && x.setIdx === setIdx)),
      }) }));
    },
    logSet: (sessionId, set) => {
      // set = { exerciseId, peId, setIdx, w, reps, rpe, ts }
      setState(s => ({ ...s, sessions: s.sessions.map(ss => ss.id !== sessionId ? ss : {
        ...ss, sets: [...ss.sets.filter(x => !(x.peId === set.peId && x.setIdx === set.setIdx)), { ...set, ts: Date.now() }]
      }) }));
    },
    // Log several sets in one commit (quick-log batches, undo of a removed
    // exercise). opts.keepTs keeps each set's own timestamp.
    logSets: (sessionId, sets, opts = {}) => {
      if (!Array.isArray(sets) || !sets.length) return;
      const now = Date.now();
      setState(s => ({ ...s, sessions: s.sessions.map(ss => {
        if (ss.id !== sessionId) return ss;
        const keys = new Set(sets.map(x => x.peId + ':' + x.setIdx));
        const fresh = sets.map((x, i) => ({ ...x, ts: opts.keepTs && x.ts ? x.ts : now + i }));
        return { ...ss, sets: [...ss.sets.filter(x => !keys.has(x.peId + ':' + x.setIdx)), ...fresh] };
      }) }));
    },
    editSet: (sessionId, idx, patch) => {
      setState(s => ({ ...s, sessions: s.sessions.map(ss => ss.id !== sessionId ? ss : {
        ...ss, sets: ss.sets.map((x, i) => i === idx ? { ...x, ...patch } : x)
      }) }));
    },
    // Drop a logged set by (peId, setIdx) from a session. Used when a set is
    // removed on the fly so any already-logged entry at the removed index
    // doesn't linger off the (now shorter) set list. No-op if nothing matches.
    removeSet: (sessionId, peId, setIdx) => {
      setState(s => ({ ...s, sessions: s.sessions.map(ss => ss.id !== sessionId ? ss : {
        ...ss, sets: ss.sets.filter(x => !(x.peId === peId && x.setIdx === setIdx))
      }) }));
    },
    // opts.saveStructure: also write the session's exercise list back to the program day.
    completeSession: (sessionId, opts = {}) => {
      // Capture side-effect payloads outside the updater so StrictMode double-invocation
      // is harmless — each invocation overwrites (not accumulates) the same values.
      const captured = { progressed: [], newPRs: [] };

      setState(s => {
        const ss = s.sessions.find(x => x.id === sessionId); if (!ss) return s;
        if (ss.completedAt) return s;           // idempotency guard (SESS-04): already completed — no-op
        const slots = sessionSlots(ss, s.programs);
        const prog = s.programs.find(p => p.id === ss.programId);
        let updatedProg = prog;
        let progressed = [];
        let changes = [];
        let delta = null;
        if (prog) {
          if (opts.saveStructure) updatedProg = programWithSessionStructure(updatedProg, ss, slots);
          // Progression engine (double progression, waves, TMs, stages). It makes
          // no changes for manual deload / "lighter day" sessions.
          const res = afterSession({ program: updatedProg, session: ss, slots, units: s.prefs.units, prefs: s.prefs, exercises: s.exercises });
          delta = progressionDelta(updatedProg, res.program);
          updatedProg = res.program;
          progressed = res.progressed || [];
          changes = res.changes || [];
        }

        // PR detection (prs.js): effective load for bodyweight / assisted lifts;
        // warm-ups, mini-sets, holds and cardio never count.
        const newPRs = [];
        const day = localDateStr(ss.startedAt || Date.now());
        const sessionBest = {};
        const bests = sessionBests(s, ss);
        for (const id of Object.keys(bests)) sessionBest[id] = { exerciseId: id, ...bests[id], date: day };
        const prsMap = Object.fromEntries(s.prs.map(p => [p.exerciseId, p]));
        Object.values(sessionBest).forEach(candidate => {
          const existing = prsMap[candidate.exerciseId];
          if (!existing || candidate.e1rm > existing.e1rm + 0.01) {
            if (existing) newPRs.push({ ...candidate, previous: existing });
            else newPRs.push({ ...candidate, first: true });
            prsMap[candidate.exerciseId] = candidate;
          }
        });

        captured.progressed = progressed;
        captured.changes = changes;
        captured.newPRs = newPRs;

        return {
          ...s,
          sessions: s.sessions.map(x => x.id !== sessionId ? x : {
            ...x, completedAt: opts.completedAt || Date.now(), exercises: slots,
            progressedExercises: progressed.map(p => ({ peId: p.peId, name: p.name, newWeight: p.to, increment: p.inc })),
            progressionChanges: changes.map(c => c.text).filter(Boolean),
            prsHit: newPRs.filter(p => !p.first).map(p => p.exerciseId),
            // What this workout changed in the program, so deleting or editing it can undo it.
            progressionDelta: delta,
          }),
          activeSessionId: null,
          programs: prog ? s.programs.map(p => p.id === prog.id ? updatedProg : p) : s.programs,
          prs: Object.values(prsMap),
        };
      });
      return captured;
    },
    updateSession: (sessionId, patch) => {
      setState(s => ({ ...s, sessions: s.sessions.map(ss => ss.id !== sessionId ? ss : { ...ss, ...patch }) }));
    },
    // Only valid path for editing a completed session's sets. PRs are rebuilt, and
    // the progression this workout caused is replayed from the corrected sets:
    // for the program's latest workout every field it moved is recomputed; for an
    // older one only the fields it moved itself (training maxes shift by the
    // difference). Guard: rejects if session is missing or not yet completed.
    updateCompletedSession: (sessionId, sets) => {
      if (!Array.isArray(sets)) {
        console.warn('updateCompletedSession: `sets` must be an array, got', typeof sets);
        return;
      }
      setState(s => {
        const ss = s.sessions.find(x => x.id === sessionId);
        if (!ss || !ss.completedAt) return s;   // guard: only completed sessions are editable here
        let edited = { ...ss, sets };
        let programs = s.programs;
        const prog = programs.find(p => p.id === ss.programId);
        const oldDelta = ss.progressionDelta !== undefined ? ss.progressionDelta : legacyDelta(ss);
        const latest = lastCompletedAt(s.sessions, ss.programId, ss.id) <= ss.completedAt;
        if (prog && !ss.deload && !ss.lighter && (latest ? ss.progressionDelta !== undefined : !!oldDelta)) {
          const before = programBefore(prog, oldDelta);
          const res = afterSession({ program: before, session: edited, slots: sessionSlots(edited, programs), units: s.prefs.units, weekIndex: ss.weekIndex ?? undefined, prefs: s.prefs, exercises: s.exercises });
          let newDelta = progressionDelta(before, res.program);
          if (!latest) newDelta = limitDelta(newDelta, oldDelta);
          const r = reconcileProgression(prog, oldDelta, newDelta);
          if (r.changed) programs = programs.map(p => p.id === prog.id ? r.program : p);
          edited = {
            ...edited,
            progressionDelta: newDelta,
            progressionChanges: (res.changes || []).map(c => c.text).filter(Boolean),
            progressedExercises: (res.progressed || []).map(p => ({ peId: p.peId, name: p.name, newWeight: p.to, increment: p.inc })),
          };
        }
        const sessions = s.sessions.map(x => x.id !== sessionId ? x : edited);
        return { ...s, sessions, programs, prs: computePRs(sessions, s.exercises, s) };
      });
    },
    // Delete a finished workout from history (PRs are rebuilt without it).
    // Its progression (target / training-max bumps) is taken back too.
    deleteSession: (sessionId) => {
      const rollback = (s) => {
        const ss = s.sessions.find(x => x.id === sessionId);
        if (!ss || !ss.completedAt) return null;
        const prog = s.programs.find(p => p.id === ss.programId);
        const delta = ss.progressionDelta !== undefined ? ss.progressionDelta : legacyDelta(ss);
        if (!prog || !delta) return null;
        const r = reconcileProgression(prog, delta, null);
        return r.changed ? r.program : null;
      };
      const undone = !!rollback(stateRef.current);
      setState(s => {
        const ss = s.sessions.find(x => x.id === sessionId);
        const sessions = s.sessions.filter(x => x.id !== sessionId);
        const prog = rollback(s);
        return {
          ...s, sessions,
          programs: prog ? s.programs.map(p => p.id === prog.id ? prog : p) : s.programs,
          deleted: ss && ss.completedAt ? withTombs(s.deleted, 'sessions', sessionId) : s.deleted,
          activeSessionId: s.activeSessionId === sessionId ? null : s.activeSessionId,
          prs: computePRs(sessions, s.exercises, s),
        };
      });
      toast(undone ? 'Workout deleted · its progression was undone' : 'Workout deleted');
    },
    cancelSession: (sessionId, opts = {}) => {
      setState(s => ({
        ...s,
        sessions: s.sessions.filter(x => x.id !== sessionId),
        activeSessionId: null,
      }));
      if (!opts.silent) toast('Session cancelled');
    },

    // ——— Body
    // One weigh-in per date: logging again on the same day replaces it.
    addBodyEntry: (entry) => {
      const id = entry.id || uid('b_');
      setState(s => {
        const replaced = s.bodyEntries.filter(b => b.date === entry.date && b.id !== id).map(b => b.id);
        return { ...s, bodyEntries: [...s.bodyEntries.filter(b => b.date !== entry.date), { id, ...entry }].sort((a,b) => a.date < b.date ? -1 : 1), deleted: withTombs(s.deleted, 'bodyEntries', replaced) };
      });
      toast('Weigh-in saved');
    },
    // Weigh-ins read from Health Connect (native.js). No toast; a day the user
    // already logged in Lift Log is never overwritten.
    importBodyEntries: (entries) => {
      setState(s => {
        const have = new Set(s.bodyEntries.map(b => b.date));
        const add = (entries || []).filter(e => e && e.date && e.weight > 0 && !have.has(e.date));
        if (!add.length) return s;
        return { ...s, bodyEntries: [...s.bodyEntries, ...add.map(e => ({ id: uid('b_'), ...e }))].sort((a,b) => a.date < b.date ? -1 : 1) };
      });
    },
    deleteBodyEntry: (id) => {
      setState(s => ({ ...s, bodyEntries: s.bodyEntries.filter(b => b.id !== id), deleted: withTombs(s.deleted, 'bodyEntries', id) }));
    },
    addMeasurement: (entry) => {
      setState(s => {
        // Upsert by date — same-day edits replace the existing entry rather than duplicating.
        const others = (s.measurements || []).filter(m => m.date !== entry.date);
        const merged = { ...entry, id: entry.id || uid('m_') };
        const replaced = (s.measurements || []).filter(m => m.date === entry.date && m.id !== merged.id).map(m => m.id);
        return {
          ...s,
          measurements: [...others, merged].sort((a, b) => a.date < b.date ? -1 : 1),
          deleted: withTombs(s.deleted, 'measurements', replaced),
        };
      });
      toast('Measurements saved');
    },
    deleteMeasurement: (id) => {
      setState(s => ({ ...s, measurements: (s.measurements || []).filter(m => m.id !== id), deleted: withTombs(s.deleted, 'measurements', id) }));
      toast('Measurement removed');
    },
    addPhoto: (photo) => {
      // Phase 17: if caller already has an id from putPhoto() (IDB), keep it.
      // Legacy callers without id get one generated here.
      const entry = {
        ...photo,
        id: photo.id || uid('ph_'),
        createdAt: photo.createdAt || new Date().toISOString(),
      };
      setState(s => ({ ...s, photos: [...s.photos, entry] }));
      toast('Photo saved');
    },
    deletePhoto: (id) => {
      setState(s => ({ ...s, photos: (s.photos || []).filter(p => p.id !== id), deleted: withTombs(s.deleted, 'photos', id) }));
      // Best-effort IDB cleanup (from src/photos.jsx)
      if (typeof window !== 'undefined' && deletePhoto) deletePhoto(id);
      toast('Photo deleted');
    },

    // ——— Prefs
    setPref: (key, value) => {
      setState(s => ({ ...s, prefs: { ...s.prefs, [key]: value } }));
    },

    // ——— Coach
    sendCoachMessage: (clientId, text) => {
      setState(s => ({
        ...s, coach: { ...s.coach, threads: { ...s.coach.threads,
          [clientId]: [...(s.coach.threads[clientId] || []), { from: 'coach', ts: 'Just now', text }]
        }, clients: s.coach.clients.map(c => c.id === clientId ? { ...c, unread: 0 } : c) }
      }));
    },
    assignProgramToClient: (clientId, programName) => {
      setState(s => ({ ...s, coach: { ...s.coach, clients: s.coach.clients.map(c => c.id === clientId ? { ...c, program: programName } : c) }}));
      toast(`Program assigned`);
    },
    addClient: (client) => {
      setState(s => ({ ...s, coach: { ...s.coach, clients: [...s.coach.clients, { ...client, id: uid('c_'), streak: 0, lastActive: 'Just invited', avatar: client.name[0], color: 'accent', status: 'on-track', unread: 0 }] }}));
      toast('Client invited');
    },

    // ——— Reset (for dev / Settings > Advanced > Reset all data)
    resetAll: () => {
      // Turn sync off first, or the next pull would bring the data straight back.
      try { disableSync(); } catch (_) {}
      try { resetSyncTracking(); } catch (_) {}
      // A reset removes everything Lift Log stored on this device: photos in
      // IndexedDB, the AI key and the coach conversation, not just the state.
      try { (stateRef.current.photos || []).forEach(ph => { try { deletePhoto && deletePhoto(ph.id); } catch (_) {} }); } catch (_) {}
      ['liftlog.ai', 'liftlog.coach', LS_KEY + '.pre-restore', persist.SIG_KEY, persist.LIVE_KEY, persist.UNDO_KEY].forEach(k => { try { localStorage.removeItem(k); } catch (_) {} });
      localStorage.removeItem(LS_KEY);
      localStorage.removeItem(ROUTE_KEY);
      // IndexedDB copy (workouts, rollback / restore copies); the next save writes the empty state.
      persist.clearAll().catch(() => {});
      setState({ ...defaultState(), hydrated: true });
      setRouteRaw({ name: 'welcome', params: {} });
    },

    // ——— Phase 24: actions.applyStateJSON — apply a validated JSON snapshot
    // (REPLACE, not MERGE). Action type: APPLY_STATE_JSON — transactional single-
    // commit replacement. Ephemeral fields (toasts, activeSessionId) are preserved
    // from current state. Pattern matches the transactional importSessions commit
    // below: one setState call → one persistence effect firing (via the useEffect
    // on [state]).
    //
    // snapshot = the `snapshot` field returned by parseStateJSON (already validated
    // for app === 'liftlog' and schemaVersion === 1). ImportJSON in settings_sub.jsx
    // is the sole caller; it invokes applyStateJSON only after a user taps
    // "Confirm restore" on the dry-run preview.
    applyStateJSON: (snapshot) => {
      const raw = (snapshot && typeof snapshot === 'object') ? snapshot : {};
      const snap = normalizeState(raw);
      const has = (k) => Array.isArray(raw[k]);
      // Keep what was on the device so the restore can be undone for 24 h (the
      // copy lives in IndexedDB, not in the localStorage quota). Undo keeps
      // workouts finished after the restore.
      persist.savePreRestore(stateRef.current).catch(() => {});
      // For sync, restored content dates from the backup: anything edited after
      // it on another device wins, and nothing the backup lacks is deleted.
      const asOf = Date.parse(raw.exportedAt || '');
      // Per-record edit times (backups from this version) date each restored record precisely.
      const syncRestore = { id: uid('rs_'), asOf: Number.isFinite(asOf) ? Math.min(asOf, Date.now()) : 1, ...(isObj(raw.times) ? { times: raw.times } : {}) };
      // A backup saved with "Include progress photos" carries them as base64.
      if (isObj(raw.photoData)) importPhotoData(raw.photoData);
      setState(prev => {
        // APPLY_STATE_JSON: replace every persistent field present in the snapshot.
        // Fields missing from the snapshot keep their current value (defensive — a
        // user-crafted backup with `include.body = false` has no bodyEntries).
        const programs = has('programs') ? snap.programs : prev.programs;
        let sessions = has('sessions') ? snap.sessions : prev.sessions;
        // A workout in progress on this device survives the restore.
        const live = prev.sessions.find(x => x.id === prev.activeSessionId && !x.completedAt);
        if (live && !sessions.some(x => x.id === live.id)) sessions = [...sessions, live];
        const exercises = has('exercises') ? snap.exercises : prev.exercises;
        let deleted = prev.deleted || {};
        for (const k of ['sessions', 'programs', 'exercises', 'bodyEntries', 'measurements']) {
          if (has(k)) deleted = withoutTombs(deleted, k, snap[k].map(x => x && x.id).filter(id => id != null));
        }
        // The restored program becomes active (ids differ across devices).
        const ids = new Set(programs.map(p => p.id));
        const activeProgramId = ids.has(raw.activeProgramId) ? raw.activeProgramId
          : ids.has(prev.activeProgramId) ? prev.activeProgramId
          : (programs[0]?.id || null);
        return {
          ...prev,
          // Persistent replaced (REPLACE semantics per DEC-json-photo-skip-v2.1 + D-20)
          sessions,
          programs,
          exercises,
          activeProgramId,
          // PRs are always rebuilt (prs.js rules; a backup's own list may predate them).
          prs:           computePRs(sessions, exercises, { ...prev, bodyEntries: has('bodyEntries') ? snap.bodyEntries : prev.bodyEntries }),
          prsVersion:    PRS_VERSION,
          // A backup carries its name, onboarding profile and onboarding status (L2).
          ...(isObj(raw.profile) ? { profile: raw.profile } : {}),
          bodyEntries:   has('bodyEntries')   ? snap.bodyEntries   : prev.bodyEntries,
          measurements:  has('measurements')  ? snap.measurements  : prev.measurements,
          prefs:         (raw.prefs && typeof raw.prefs === 'object') ? { ...prev.prefs, ...snap.prefs } : prev.prefs,
          user:          (raw.user && typeof raw.user === 'object')
            ? { ...prev.user, pro: !!raw.user.pro, ...(typeof raw.user.name === 'string' ? { name: raw.user.name } : {}) }
            : prev.user,
          // Photos: kept; a backup that embeds photos adds the ones this device lacks.
          photos:        restoredPhotos(prev.photos, raw),
          // Ephemeral preserved — these are UI/runtime state, not part of the backup.
          toasts:          prev.toasts,
          activeSessionId: live ? live.id : null,
          welcomed:        true, // a restore implies the user is past onboarding
          deleted,
          syncRestore,
        };
      });
    },
    // Sync: atomic state replacement computed from the current state.
    applySyncState: (fn) => setState(fn),
    // Put back what was on the device before the last restore.
    // Undo is offered for 24 h after a restore; it keeps workouts finished since
    // the restore and the live workout. → { at, until, sessions } | null
    undoRestoreInfo: () => { const u = persist.undoInfo(); return u && !u.expired ? u : null; },
    canUndoRestore: () => { const u = persist.undoInfo(); return !!(u && !u.expired); },
    undoRestore: async () => {
      let pre = null;
      try { pre = await persist.readPreRestore(); } catch (_) {}
      if (!pre) { toast('Nothing to undo — the restore is more than a day old'); return false; }
      try {
        const prevState = normalizeState(pre.data);
        let kept = 0;
        setState(cur => {
          const m = persist.mergeUndo(prevState, cur, pre.at);
          kept = m.kept; delete m.kept;
          // Merges back for sync (dated "old"): never overrides newer edits elsewhere.
          return completeShape({ ...defaultState(), ...m, prsVersion: 0, toasts: cur.toasts, hydrated: true, syncRestore: { id: uid('rs_'), asOf: 1 } });
        });
        await persist.dropPreRestore();
        toast(kept ? `Restore undone · kept ${kept} workout${kept === 1 ? '' : 's'} logged since` : 'Restore undone');
        return true;
      } catch (_) { return false; }
    },
    // In-memory backup (storage full / crash rescue), restorable with Import JSON.
    backupJSON: () => backupJSONOf(stateRef.current),
    // Ask the browser not to evict our storage (called on the first workout).
    requestPersist: () => { _persistAsked = false; askPersist(); },

    // ——— Phase 20: import sessions from CSV (transactional all-or-nothing)
    // parsed = { sessions: [...], exercisesToCreate: [...] }
    // Returns { sessionsAdded, setsAdded, exercisesAdded, duplicatesSkipped }
    importSessions: (parsed) => {
      const sessions = Array.isArray(parsed?.sessions) ? parsed.sessions : [];
      const newExercises = Array.isArray(parsed?.exercisesToCreate) ? parsed.exercisesToCreate : [];
      let result = { sessionsAdded: 0, setsAdded: 0, exercisesAdded: 0, duplicatesSkipped: 0, invalidSkipped: 0 };
      setState(prev => {
        // Same id = same workout (re-importing your own export). Otherwise the
        // fingerprint is day + start time + the exercises done — never a NaN
        // date (that once made every undated row a "duplicate").
        result = { sessionsAdded: 0, setsAdded: 0, exercisesAdded: 0, duplicatesSkipped: 0, invalidSkipped: 0 };
        const fpOf = (s) => `${localDateStr(s.startedAt)}|${Math.round(s.startedAt / 60000)}|${[...new Set((s.sets || []).map(x => x.exerciseId))].sort().join(',')}`;
        const okTime = (t) => typeof t === 'number' && Number.isFinite(t) && t > 0;
        // Exercises first: one the library already has under another id (same
        // name) is reused, and the imported sets are pointed at it.
        const existingExIds = new Set((prev.exercises || []).map(e => e.id));
        const exByName = new Map((prev.exercises || []).map(e => [String(e.name || '').toLowerCase(), e.id]));
        const remap = new Map();
        const uniqueExercises = [];
        for (const ex of newExercises) {
          if (!isObj(ex) || !ex.id) continue;
          if (existingExIds.has(ex.id)) continue;
          const same = exByName.get(String(ex.name || '').toLowerCase());
          if (same != null) { remap.set(ex.id, same); continue; }
          uniqueExercises.push(ex);
          existingExIds.add(ex.id); exByName.set(String(ex.name || '').toLowerCase(), ex.id);
          result.exercisesAdded++;
        }
        const existingIds = new Set((prev.sessions || []).map(s => s.id));
        const existingFp = new Set((prev.sessions || []).filter(s => okTime(s.startedAt)).map(fpOf));
        const uniqueSessions = [];
        for (const raw of sessions) {
          if (!isObj(raw) || !raw.id || !okTime(raw.startedAt) || !okTime(raw.completedAt) || !(raw.sets || []).length) { result.invalidSkipped++; continue; }
          let s = normSession(raw);
          if (remap.size && s.sets.some(x => remap.has(x.exerciseId))) s = { ...s, sets: s.sets.map(x => remap.has(x.exerciseId) ? { ...x, exerciseId: remap.get(x.exerciseId) } : x) };
          if (existingIds.has(s.id)) { result.duplicatesSkipped++; continue; }
          const fp = fpOf(s);
          if (existingFp.has(fp)) { result.duplicatesSkipped++; continue; }
          uniqueSessions.push(s);
          existingIds.add(s.id);
          existingFp.add(fp);
          result.sessionsAdded++;
          result.setsAdded += (s.sets || []).length;
        }
        const allSessions = [...(prev.sessions || []), ...uniqueSessions].sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0));
        const allExercises = [...(prev.exercises || []), ...uniqueExercises];
        return {
          ...prev,
          sessions: allSessions,
          exercises: allExercises,
          // Imported history can hold new records.
          prs: uniqueSessions.length ? computePRs(allSessions, allExercises, prev) : prev.prs,
        };
      });
      return result;
    },
  }), [replaceRoute, toast]);

  actionsRef.current = actions;
  const dismissStorageFull = React.useCallback(() => setStorageFull(false), []);
  const backupNow = React.useCallback(() => backupJSONOf(stateRef.current), []);
  const value = React.useMemo(() => ({ state, actions, route, setRoute, goBack, replaceRoute, toast, runToastAction, storageFull, dismissStorageFull, corruptKey, backupNow }),
    [state, actions, route, setRoute, goBack, replaceRoute, toast, runToastAction, storageFull, dismissStorageFull, corruptKey, backupNow]);

  // No snapshot to render from (big history, cleared localStorage): a blank
  // screen in the app's background for the few ms IndexedDB takes.
  if (waitForData && !state.hydrated) return <div style={{ position: 'fixed', inset: 0, background: state.prefs?.theme === 'light' ? '#f6f6f3' : '#0b0b0c' }} aria-busy="true" />;
  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

function useApp() {
  const ctx = React.useContext(AppCtx);
  if (!ctx) throw new Error('useApp must be inside AppProvider');
  return ctx;
}

// Convenience: get active program & day
function useActiveProgram() {
  const { state } = useApp();
  return state.programs.find(p => p.id === state.activeProgramId) || state.programs[0];
}

// Unit formatter — stored values are always lb; `units` controls display ('lb' | 'kg').
function fmtWeight(w, units) {
  if (w === 0 || w == null) return '0';
  const v = units === 'kg' ? w * 0.45359237 : w;
  if (Math.abs(v - Math.round(v)) < 0.05) return String(Math.round(v));
  return v.toFixed(1);
}
function unitLabel(units) { return units === 'kg' ? 'kg' : 'lb'; }

export { normalizeState, LS_KEY, historyOf, rescueBackupJSON };
export { AppProvider, useApp, useActiveProgram, AppCtx, BUILTIN_EXERCISES, MUSCLE_GROUPS_ALL, EQUIPMENT_ALL, STARTER_PROGRAMS, fmtWeight, unitLabel, uid };
